/*
 * Web Push subscription helper.
 *
 * Handles the client side of the Push API:
 *   - Detects if push is supported at all.
 *   - Reads current permission + subscription state.
 *   - Subscribes to the browser push endpoint using the server's VAPID public key.
 *   - Sends the subscription to the backend.
 *   - Unsubscribes locally and on the server.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getNotificationPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

export async function getExistingSubscription() {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error("Tu navegador no soporta notificaciones push.");
  }
  // Ensure the service worker is registered
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    reg = await navigator.serviceWorker.register("/service-worker.js");
    await navigator.serviceWorker.ready;
  }

  // Ask permission
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    throw new Error(
      perm === "denied"
        ? "Has bloqueado las notificaciones. Actívalas en los ajustes del navegador."
        : "No se otorgó permiso para las notificaciones."
    );
  }

  // Fetch VAPID public key
  const { data } = await axios.get(`${API}/push/vapid-public-key`, {
    withCredentials: true,
  });
  const publicKey = data.public_key;
  if (!publicKey) throw new Error("No se pudo obtener la clave del servidor.");

  // Subscribe
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const payload = sub.toJSON();
  await axios.post(`${API}/push/subscribe`, payload, { withCredentials: true });
  return sub;
}

export async function unsubscribeFromPush() {
  const sub = await getExistingSubscription();
  if (!sub) return true;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    /* ignore */
  }
  try {
    await axios.post(
      `${API}/push/unsubscribe`,
      { endpoint },
      { withCredentials: true }
    );
  } catch {
    /* ignore */
  }
  return true;
}

export async function sendTestPush() {
  await axios.post(`${API}/push/test`, {}, { withCredentials: true });
}
